import type { CSSProperties } from 'react'

interface StepperProps {
  value: number | string
  /** Previous value — renders trailing "was {N}" label (Rule 09: edits leave a trail). */
  was?: number | string
  unit?: string
  onDecrement?: () => void
  onIncrement?: () => void
  style?: CSSProperties
}

/**
 * Numeric stepper with −/+ controls.
 * Optional `was` prop renders a trailing mono "was N" label for edited rows (Rule 09).
 */
export function Stepper({
  value,
  was,
  unit,
  onDecrement,
  onIncrement,
  style,
}: StepperProps): JSX.Element {
  const btnStyle: CSSProperties = {
    width: 28,
    height: 28,
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--text)',
    borderRadius: 'var(--r-sm)',
    minHeight: 44,
    minWidth: 44,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  return (
    <span
      data-testid="stepper"
      style={{
        display: 'inline-flex',
        gap: 4,
        alignItems: 'center',
        ...style,
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          background: 'var(--surface)',
          border: '1px solid var(--line-strong)',
          borderRadius: 'var(--r-md)',
          padding: 2,
        }}
      >
        <button
          type="button"
          aria-label="Decrement"
          data-testid="stepper-decrement"
          onClick={onDecrement}
          style={btnStyle}
        >
          −
        </button>
        <span
          data-testid="stepper-value"
          style={{
            width: 48,
            textAlign: 'center',
            fontFamily: 'var(--font-mono)',
            fontSize: 18,
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            color: 'var(--text)',
            padding: '4px 0',
          }}
        >
          {value}
          {unit ?? ''}
        </span>
        <button
          type="button"
          aria-label="Increment"
          data-testid="stepper-increment"
          onClick={onIncrement}
          style={btnStyle}
        >
          +
        </button>
      </span>
      {was != null && (
        <span
          data-testid="stepper-was"
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            color: 'var(--text-3)',
            marginLeft: 6,
          }}
        >
          was {was}
          {unit ?? ''}
        </span>
      )}
    </span>
  )
}
